import { CARD_CHROME, cn, Skeleton } from "@healthos/ui";
import { PageLayout } from "@healthos/ui/shell";
import type { CSSProperties } from "react";

// Page-shaped Suspense fallback so a slow route chunk never renders blank.
// Mirrors the shared page anatomy (PageLayout, eyebrow, title,
// subtitle, content cards) so the skeleton-to-page swap does not shift layout.
//
// The bars are static and each block carries the skeleton-sheen sweep, the
// same loading language as every page's own skeleton. This fallback is often
// on screen for only a few frames before a page skeleton replaces it, and a
// bar that breathes on its own rhythm would make that handover a brightness
// blink.
//
// Deliberately does NOT carry pv-head/pv-hero: this is a single fallback
// shared by every route, so it can be mid-transition (claiming those named
// view-transition groups) for a page that never uses them (Snapshot,
// Community Moments) at the exact moment the real page swaps in - a named
// group with no claimant on one side is part of the WebKit crash condition
// this app hit. The reveal itself still animates once the real page mounts
// and runs its own useRevealSwap/equivalent; only this shared loading
// skeleton opts out.
function sheen(step: number): CSSProperties {
  return { "--skeleton-sheen-delay": `${step * 140}ms` } as CSSProperties;
}

export function RouteFallback() {
  return (
    <PageLayout aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading this page.</span>
      <div aria-hidden="true">
        <div className="skeleton-sheen" style={sheen(0)}>
          <div className="mb-3.5 flex items-center gap-2">
            <span className="h-[6px] w-[6px] rounded-full bg-mist-dark" />
            <Skeleton className="h-[10px] w-[180px] max-w-full rounded-full" />
          </div>
          <Skeleton className="mb-2 h-9 w-3/5 max-w-full rounded-lg" />
          <Skeleton className="h-3.5 w-4/5 max-w-[56ch] rounded-full" />
        </div>

        <div className="mt-8 space-y-4">
          {[0, 1].map((i) => (
            <div
              key={i}
              className={cn(CARD_CHROME, "skeleton-sheen h-52 p-6")}
              style={sheen(i + 1)}
            >
              <Skeleton className="h-[10px] w-32 rounded-full" />
              <Skeleton className="mt-4 h-5 w-1/2" />
              <div className="mt-3 space-y-2">
                <Skeleton className="h-3.5 w-11/12 rounded-full" />
                <Skeleton className="h-3.5 w-8/12 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </PageLayout>
  );
}
